// screens/MessagesScreen.tsx
//
// W6 v1.1 Session 2 — student-side inbox for instructor messages.
// V28 S6 restyle (2026-06-08): per-source colored avatars, capitalized role +
// natural-Italian relative time, prototype read/unread treatment, and the
// read-only "Sola lettura" footer. Read-only v1 — NO reply composer / send
// affordance (founder-decided). Light-only; hamburger LEFT; gesture back.
// Maps to docs/specs/v28-prototype/screens/messaggi.html.

import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { ProfProse } from '../components/ProfProse';
import { ProfMark } from '../components/ProfAvatar';
import { useTokens } from '../state/theme';
import { getStoredProfile } from '../state/profile';
import { useUnreadMessages } from '../state/unread-messages';
import { relativeTimeIt } from '../utils/relative-time-it';
import { safeLinkPath } from '../utils/safe-link-path';
import { messagesApi, type InboxMessage } from '../api/messages';

// FP8 — CTA label by destination prefix, Italian "tu" voice. Neutral lesson
// label (never "Fast"/"Deep") per one-adaptive-lesson.
function ctaLabel(path: string): string {
  if (path.startsWith('/studia/argomento/') || path.startsWith('/studia/lezione/')) return 'Studia l’argomento';
  if (path.startsWith('/simula')) return 'Inizia una simulazione';
  if (path.startsWith('/progresso')) return 'Vedi i tuoi progressi';
  if (path.startsWith('/appendice')) return 'Apri l’Appendice';
  if (path.startsWith('/prof')) return 'Chiedi a Il Prof';
  return 'Apri';
}

// Prototype shows capitalized role labels on the second line
// (messaggi.html lines 30/43/53/68: "Titolare" / "Istruttore di teoria" / "Segreteria").
const ROLE_LABELS: Record<InboxMessage['author_role'], string> = {
  titolare:           'Titolare',
  instructor_teoria:  'Istruttore di teoria',
  instructor_guida:   'Istruttore di guida',
  segreteria:         'Segreteria',
};

type SourceKey = 'titolare' | 'istruttore' | 'segreteria';

/** author_role → the prototype's source-coded avatar family (index.html 1271-1274). */
function sourceKeyForRole(role: InboxMessage['author_role']): SourceKey {
  if (role === 'titolare') return 'titolare';
  if (role === 'segreteria') return 'segreteria';
  return 'istruttore'; // instructor_teoria + instructor_guida
}

/** Derive a 1–2 char avatar monogram from whatever name parts are present. */
function avatarInitials(m: InboxMessage): string {
  const first = (m.author_first_name || '').trim();
  const last  = (m.author_last_name  || '').trim();
  const fi = first.charAt(0);
  const li = last.charAt(0);
  const initials = `${fi}${li}`.toUpperCase();
  if (initials) return initials;
  // No person name at all (e.g. an unnamed segreteria sender): fall back to the
  // capitalized role's first two letters ("Segreteria" → "SE").
  return ROLE_LABELS[m.author_role].slice(0, 2).toUpperCase();
}

export default function MessagesScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<InboxMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    messagesApi.list()
      .then((p) => { if (alive) setMessages(p.messages); })
      .catch(() => { if (alive) setError('Non sono riuscito a caricare i tuoi messaggi. Riprova tra un attimo.'); });
    return () => { alive = false; };
  }, []);

  const unreadCount = useMemo(
    () => (messages ?? []).filter((m) => !m.delivered_at).length,
    [messages],
  );

  // Dynamic chrome subtitle: school (when the profile carries one) + the live
  // unread count. We never invent a school name — the prototype's "DajeTutta"
  // is fixture copy; here it renders only `getStoredProfile().school` if set.
  const subtitle = useMemo(() => {
    const school = getStoredProfile().school?.trim();
    const unreadLabel =
      messages === null ? null
      : unreadCount === 0 ? 'Tutto letto'
      : unreadCount === 1 ? '1 non letto'
      : `${unreadCount} non letti`;
    if (school && unreadLabel) return `${school} · ${unreadLabel}`;
    return school || unreadLabel || undefined;
  }, [messages, unreadCount]);

  async function markReadLocal(m: InboxMessage) {
    if (m.delivered_at) return;
    try {
      await messagesApi.markRead(m.id);
      setMessages((prev) => prev ? prev.map(x => x.id === m.id ? { ...x, delivered_at: new Date().toISOString() } : x) : prev);
      // FP8 — keep the hamburger unread badge in sync without a refetch.
      useUnreadMessages.getState().decrement();
    } catch {
      // Soft-fail; the next reload will reconcile.
    }
  }

  function handleClick(m: InboxMessage) {
    void markReadLocal(m);
  }

  function handleCta(m: InboxMessage, dest: string) {
    void markReadLocal(m);
    navigate(dest);
  }

  // FP8 — a system retention nudge renders with the Il Prof identity (the
  // author FK carries an empty name + a placeholder role purely to satisfy the
  // constraint; it must NOT be shown as that role).
  function isSystem(m: InboxMessage): boolean {
    return m.kind === 'system';
  }

  function authorName(m: InboxMessage): string {
    if (isSystem(m)) return 'Il Prof';
    const last = m.author_last_name ? ` ${m.author_last_name}` : '';
    return `${m.author_first_name}${last}`.trim();
  }

  // Avatar fill by source family — mirrors index.html 1271-1274 via tokens.
  function avatarStyle(role: InboxMessage['author_role']): CSSProperties {
    const key = sourceKeyForRole(role);
    if (key === 'titolare') {
      return { background: tokens.brandGradient, color: '#fff' };
    }
    if (key === 'segreteria') {
      return { background: tokens.warnSoft, color: tokens.warnText };
    }
    return { background: tokens.rightSoft, color: tokens.rightText }; // istruttore (sage)
  }

  return (
    <Screen>
      <TopChrome
        title="Messaggi"
        subtitle={subtitle}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 80px' }}>
        {error && (
          <div role="alert" style={{ color: tokens.wrong, fontSize: 14, padding: 12 }}>{error}</div>
        )}
        {messages === null && !error && (
          <div style={{ color: tokens.textMuted, fontSize: 14, padding: 12 }}>Carico i messaggi…</div>
        )}
        {messages !== null && messages.length === 0 && (
          /* V29 S7 (audit A6): a warm starting-line, not a bare sad zero. */
          <div
            data-testid="messages-empty"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 10,
              padding: '48px 24px',
              color: tokens.textMuted,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 56,
                height: 56,
                borderRadius: 18,
                background: tokens.accentSoft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.accentInk,
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
            </span>
            <p style={{ margin: 0, fontFamily: tokens.fonts.body, fontSize: 15, fontWeight: 700, color: tokens.text }}>
              Ancora nessun messaggio
            </p>
            <p style={{ margin: 0, fontFamily: tokens.fonts.body, fontSize: 13.5, lineHeight: 1.5, maxWidth: 300 }}>
              Quando la tua autoscuola o Il Prof avranno qualcosa da dirti, lo troverai qui.
            </p>
          </div>
        )}
        {messages !== null && messages.length > 0 && (
          <>
            <ul data-testid="messages-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.map((m) => {
                const unread = !m.delivered_at;
                const when = relativeTimeIt(m.created_at);
                const sys = isSystem(m);
                // FP8 — allow-listed in-app deep-link CTA (null when absent or
                // not allow-listed; the validator rejects external/unsafe paths).
                const dest = safeLinkPath(m.link_path);
                return (
                  <li key={m.id}>
                    <div
                      style={{
                        width: '100%',
                        background: tokens.surface,
                        border: `1px solid ${unread ? tokens.accentBorder : tokens.line}`,
                        borderRadius: tokens.radius.lg,
                        overflow: 'hidden',
                        // Prototype: read rows dim to 0.66 (index.html 1270).
                        opacity: unread ? 1 : 0.66,
                      }}
                    >
                      {/* Primary card action and deep-link CTA are sibling
                          buttons: both are keyboard-native, with no nested
                          interactive control or duplicate bubbling path. */}
                      <button
                        type="button"
                        data-testid={`messages-item-${m.id}`}
                        onClick={() => handleClick(m)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick(m);
                          }
                        }}
                        aria-label={unread ? `${authorName(m)}, messaggio non letto` : authorName(m)}
                        style={{
                          display: 'flex',
                          gap: 12,
                          alignItems: 'flex-start',
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          color: 'inherit',
                          border: 'none',
                          padding: '14px 16px',
                          cursor: 'pointer',
                          fontFamily: tokens.fonts.body,
                        }}
                      >
                      {/* Avatar: system nudges wear the brand-gradient Prof mark
                          (Il Prof identity); instructor rows keep the per-source
                          colored square avatar. */}
                      {sys ? (
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 38,
                            height: 38,
                            minWidth: 38,
                            borderRadius: 11,
                            background: tokens.brandGradient,
                          }}
                        >
                          <ProfMark size={22} />
                        </span>
                      ) : (
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 38,
                            height: 38,
                            minWidth: 38,
                            borderRadius: 11,
                            fontWeight: 800,
                            fontSize: 13,
                            ...avatarStyle(m.author_role),
                          }}
                        >
                          {avatarInitials(m)}
                        </span>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            {/* FP8 — "Promemoria" tag before the Il Prof identity. */}
                            {sys && (
                              <span
                                data-testid={`messages-item-${m.id}-system`}
                                style={{
                                  flexShrink: 0,
                                  background: tokens.accentSoft,
                                  color: tokens.accentInk,
                                  fontFamily: tokens.fonts.mono,
                                  fontSize: 9.5,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  textTransform: 'uppercase',
                                  padding: '2px 7px',
                                  borderRadius: tokens.radius.pill,
                                }}
                              >
                                Promemoria
                              </span>
                            )}
                            <span style={{ fontSize: 14, color: tokens.text, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {authorName(m)}
                            </span>
                          </span>
                          {unread && (
                            <span
                              aria-label="non letto"
                              data-testid={`messages-item-${m.id}-unread`}
                              style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                borderRadius: '50%',
                                background: tokens.accent,
                              }}
                            />
                          )}
                        </header>
                        {/* System rows omit the instructor role/time second line. */}
                        {!sys && (
                          <div style={{ fontSize: 11, color: tokens.textFaint, marginBottom: 5 }}>
                            {when ? `${ROLE_LABELS[m.author_role]} · ${when}` : ROLE_LABELS[m.author_role]}
                          </div>
                        )}
                        {/* Body: system nudges render markdown via ProfProse;
                            instructor messages stay plain pre-wrap text. */}
                        {sys ? (
                          <div data-testid={`messages-item-${m.id}-body`} style={{ fontSize: 14, color: unread ? tokens.text : tokens.textMuted, wordBreak: 'break-word' }}>
                            <ProfProse text={m.body_md} serif={false} size={14} />
                          </div>
                        ) : (
                          <div data-testid={`messages-item-${m.id}-body`} style={{ fontSize: 14, color: unread ? tokens.text : tokens.textMuted, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {m.body_md}
                          </div>
                        )}
                      </div>
                    </button>
                    {/* FP8 — allow-listed deep-link CTA remains visually
                        inside the card, but is a sibling of its primary action. */}
                    {dest && (
                      <div style={{ padding: '0 16px 14px 66px' }}>
                        <button
                          type="button"
                          data-testid={`messages-item-${m.id}-cta`}
                          onClick={() => handleCta(m, dest)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCta(m, dest);
                            }
                          }}
                          style={{
                            background: tokens.accent,
                            color: '#fff',
                            border: 'none',
                            borderRadius: tokens.radius.pill,
                            padding: '8px 16px',
                            minHeight: 36,
                            cursor: 'pointer',
                            fontFamily: tokens.fonts.body,
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {ctaLabel(dest)}
                        </button>
                      </div>
                    )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Read-only note (prototype line 74) — no reply/send affordance. */}
            <p
              data-testid="messages-readonly-note"
              style={{ textAlign: 'center', fontSize: 11, color: tokens.textFaint, margin: '16px 0 0' }}
            >
              I messaggi arrivano dalla tua autoscuola. Sola lettura.
            </p>
          </>
        )}
      </div>
    </Screen>
  );
}
